/* Tenant-code provider.
 *
 * Tenant code is resolved from the URL path (`/:tenantCode/...`) per the
 * external `/api/{tenantCode}/v1/` contract.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';

interface TenantContextValue {
  tenantCode: string;
}

const TenantContext = createContext<TenantContextValue | null>(null);

interface TenantProviderProps {
  children: ReactNode;
  /**
   * Explicit chrome tenant (e.g. platform `/shops` shell using URL slug).
   * When set, wins over the `:tenantCode` route param.
   */
  tenantCode?: string;
}

/**
 * Playwright-only overlay — see `window.__RPAPP_E2E_TENANT_OVERRIDE__`.
 * DEV/e2e bridge — same NODE_ENV gate as ToastHost `__RPAPP_E2E_SHOW_TOAST__`.
 * Avoid `import.meta` — Jest cannot parse it in this module graph.
 */
function resolveE2eTenantOverride(): string | undefined {
  if (process.env.NODE_ENV === 'production') {
    return undefined;
  }
  if (typeof window === 'undefined') {
    return undefined;
  }
  const raw = window.__RPAPP_E2E_TENANT_OVERRIDE__;
  if (typeof raw !== 'string') {
    return undefined;
  }
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function TenantProvider({
  children,
  tenantCode: tenantCodeProp,
}: TenantProviderProps): JSX.Element {
  const params = useParams<{ tenantCode?: string }>();
  const fromProp = tenantCodeProp?.trim();
  const fromRoute = params.tenantCode?.trim();
  const e2eOverride = resolveE2eTenantOverride();
  const tenantCode =
    e2eOverride ??
    (fromProp !== undefined && fromProp.length > 0 ? fromProp : fromRoute);
  if (tenantCode === undefined || tenantCode.length === 0) {
    throw new Error(
      'TenantProvider requires a :tenantCode route param or tenantCode prop',
    );
  }
  const value = useMemo<TenantContextValue>(() => ({ tenantCode }), [tenantCode]);
  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenantCode(): string {
  const ctx = useContext(TenantContext);
  if (ctx === null) {
    throw new Error('useTenantCode must be used inside <TenantProvider>');
  }
  return ctx.tenantCode;
}
